import { useEffect } from 'react';
import { coverMakerHeightMessage, embedHeightFor, embedTargetOrigin } from './embedHeight';

/** The height message of a tool for the width of its frame. */
export type EmbedHeightMessageFor = (frameWidth: number) => { type: string; height: number };

/** The cover maker message (music24-website `coverMakerEmbed.ts`). */
const coverMakerMessageFor: EmbedHeightMessageFor = (frameWidth) => coverMakerHeightMessage(embedHeightFor(frameWidth));

/**
 * In embed mode (`?embed=1`), tells the page around the frame how tall the tool is: on mount and
 * when the width of the frame changes. The tool fills its frame, so the height comes from the
 * width, not from the content. The message goes to the origin of the page (`document.referrer`),
 * else to `*`: it holds only a height.
 *
 * `messageFor` builds the message: the cover maker by default (the contract of music24-website
 * `coverMakerEmbed.ts`); the Studio showcase passes its own (`showcaseEmbedHeight.ts`). Pass a
 * function defined outside the component, so the effect does not run on each render.
 */
export function useEmbedHeight(enabled: boolean, messageFor: EmbedHeightMessageFor = coverMakerMessageFor): void {
  useEffect(() => {
    if (!enabled || typeof window === 'undefined' || window.parent === window) {
      return undefined;
    }
    const target = embedTargetOrigin(document.referrer);
    const post = (): void => {
      window.parent.postMessage(messageFor(window.innerWidth), target);
    };
    post();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', post);
      return () => window.removeEventListener('resize', post);
    }
    const observer = new ResizeObserver(post);
    observer.observe(document.documentElement);
    return () => observer.disconnect();
  }, [enabled, messageFor]);
}
